import {
  Configuration,
  CreditMemoModel,
  FileUri,
  FileUriODataResultCollection,
  InvoiceModel,
  InvoiceModelODataResultCollection,
  CreditMemoApi,
  InvoiceApi,
} from "api";
import { ApiStatusException } from "services/apiErrors";
import {
  andFilters,
  apiResponseTransformer,
  baseApiConfig,
  invalidStatusCode,
  sortToString,
  TransformedApiResponse,
} from "./apiHelpers";
import { formatNumber, valueOr } from "util/statHelpers";
import filterFactories from "./filterFactories";

class InvoiceService {
  private invoiceApi!: InvoiceApi;
  private creditMemoApi!: CreditMemoApi;

  constructor(config: Configuration = baseApiConfig) {
    this.invoiceApi = new InvoiceApi(config);
    this.creditMemoApi = new CreditMemoApi(config);
  }

  private static mapApiInvoiceLineItem(
    value: InvoiceModel | CreditMemoModel
  ): InvoiceLineItem {
    return {
      docEntry: value.DocEntry ?? 0,
      releaseNumber: valueOr(value.ReleaseNumber),
      invoiceNumber: valueOr(value.DocumentNumber),
      dueDate: value.DocDueDate
        ? new Date(value.DocDueDate)
        : valueOr(undefined),
      amountDue: value.CalculatedAmountDue ?? 0,
      amountDueFormatted: valueOr(
        value.CalculatedAmountDue !== undefined
          ? formatNumber(value.CalculatedAmountDue)
          : undefined
      ),
      poNumber: valueOr(value.NumAtCard),
      currency: valueOr(value.Currency),
      isCredit: value.ObjectType === "oCreditNotes",
    };
  }

  private static mapApiFileUri(fileUri: FileUri): ReleaseFile {
    return {
      category: valueOr(fileUri.Category),
      date: valueOr(fileUri.Date ? new Date(fileUri.Date) : undefined),
      filename: valueOr(fileUri.FileName),
      mimeType: valueOr(fileUri.Mime ?? "application/pdf"),
      url: fileUri.Uri ?? undefined,
    };
  }

  private static buildInvoicesListFilters = (
    contractNumber: number,
    { dueDate, amountDue }: InvoiceFilters
  ) => {
    const { compare } = filterFactories;
    return andFilters([
      compare("ContractNumber").eq(contractNumber),
      amountDue ? compare("AmountDue").gt(0) : null,
      dueDate
        ? andFilters([
            compare("AmountDue").gt(0),
            compare("DocDueDate").lt(new Date().toISOString()),
          ])
        : null,
    ]);
  };

  async listInvoices(
    contractNumber: number,
    searchTerm: string,
    filters: InvoiceFilters = {
      amountDue: false,
      dueDate: false,
    },
    sort?: { key: keyof InvoiceModel; direction: string }
  ): Promise<
    TransformedApiResponse<InvoiceModelODataResultCollection, InvoiceLineItem[]>
  > {
    return this.invoiceApi
      .apiInvoiceSearchContractDetailsGet({
        searchString: searchTerm,
        $filter: InvoiceService.buildInvoicesListFilters(
          contractNumber,
          filters
        ),
        $expand: "DocumentLines",
        $orderby: sortToString(sort),
      })
      .then(apiResponseTransformer)
      .then((data) => {
        if (!data) {
          throw new Error("Invoices not found");
        } else if (invalidStatusCode(data.statusCode)) {
          throw new ApiStatusException(data.statusText, data.statusCode);
        }
        return {
          ...data,
          value: data.value?.map(InvoiceService.mapApiInvoiceLineItem) ?? [],
        };
      });
  }

  getReleaseFileList(
    docEntry: number,
    isCredit?: boolean
  ): Promise<
    TransformedApiResponse<FileUriODataResultCollection, ReleaseFile[]>
  > {
    const result = isCredit
      ? this.creditMemoApi.apiCreditMemoGetFileUriListDocEntryGet({
          docEntry,
          $top: "50",
        })
      : this.invoiceApi.apiInvoiceGetFileUriListDocEntryGet({
          docEntry,
          $top: "50",
        });
    return result.then(apiResponseTransformer).then((data) => {
      if (!data) {
        throw new Error("Files not found");
      } else if (invalidStatusCode(data.statusCode)) {
        throw new ApiStatusException(data.statusText, data.statusCode);
      }
      return {
        ...data,
        value: data.value?.map(InvoiceService.mapApiFileUri) ?? [],
      };
    });
  }
}

export default InvoiceService;
